import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../AuthContext';
import { C, S, getStatusBadgeStyle, formatDate, formatDateTime, fmtHours, formatCurrency } from '../styles';
import { fetchLabourRates, calcDayworkSheetCost } from '../labourRates';
import { AttachmentUploader } from '../components/AttachmentUploader';
import { generateInstructionPDF } from '../utils/generateInstructionPDF';
import { sendEmail } from '../utils/emailService';
import { ewnSubmissionEmail, cviSubmissionEmail } from '../utils/emailTemplates';

const EWN_WORDING = `EARLY WARNING NOTICE

This document serves as an Early Warning Notice pursuant to the contract conditions. The Contractor hereby gives notice of a matter which could:
(a) increase the total of the Prices,
(b) delay Completion or a Key Date,
(c) delay meeting a Condition for a bonus, or
(d) impair the performance of the works in use.

The Subcontractor reserves all rights and entitlements under the subcontract. This notice is issued without prejudice to any other rights and remedies available. The works described herein fall outside the agreed subcontract scope and will require separate assessment and valuation.`;

const CVI_WORDING = `CONFIRMATION OF VERBAL INSTRUCTION

This document confirms a verbal instruction received on the date and time stated above. The Subcontractor has carried out or will carry out the instructed works under protest, reserving all rights to recover additional costs and extensions of time as appropriate.

The works described herein constitute a change to the agreed subcontract scope. The Subcontractor hereby notifies the Main Contractor that costs will be tracked and a variation account will be submitted for agreement. All additional resources deployed shall be recorded on contemporaneous daywork sheets.`;

const IR_WORDING = `INSTRUCTION RECEIVED

This records a formal instruction received from the Main Contractor. The attached document(s) constitute the instruction. Proline Internals Ltd will proceed in accordance with the instruction subject to agreement of any additional costs and/or time.`;

const WORDING = { EWN: EWN_WORDING, CVI: CVI_WORDING, IR: IR_WORDING };

const TYPE_STYLE = {
  EWN: { background: '#dbeafe', color: '#1e40af' },
  CVI: { background: '#fce7f3', color: '#9d174d' },
  IR:  { background: '#d1fae5', color: '#065f46' },
};

const TYPE_LABEL = {
  EWN: 'Early Warning Notice',
  CVI: 'Confirmation of Verbal Instruction',
  IR:  'Instruction Received',
};

const IMPACT_LABELS = {
  cost: 'Cost',
  programme: 'Programme / Time',
  quality: 'Quality',
  health_safety: 'Health & Safety',
  design: 'Design',
};

const IMPACT_COLOURS = {
  cost:          { bg: '#fffbeb', color: '#92400e', border: '#fde68a' },
  programme:     { bg: '#eff6ff', color: '#1e40af', border: '#bfdbfe' },
  quality:       { bg: '#f5f3ff', color: '#6d28d9', border: '#ddd6fe' },
  health_safety: { bg: '#fef2f2', color: '#991b1b', border: '#fecaca' },
  design:        { bg: '#ecfdf5', color: '#065f46', border: '#6ee7b7' },
};

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function FileIcon({ type }) {
  const color = type === 'application/pdf' ? '#ef4444'
    : type?.startsWith('image/') ? '#8b5cf6'
    : '#3b82f6';
  const label = type === 'application/pdf' ? 'PDF'
    : type?.startsWith('image/') ? 'IMG'
    : 'DOC';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: '36px', height: '36px', borderRadius: '6px',
      background: color + '20', color, fontSize: '10px', fontWeight: '700',
      flexShrink: 0,
    }}>
      {label}
    </span>
  );
}

function InfoRow({ label, value, valueStyle }) {
  return (
    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', fontSize: '14px', flexWrap: 'wrap' }}>
      <span style={{ fontWeight: '600', color: C.textMuted, minWidth: '180px' }}>{label}</span>
      <span style={{ color: C.text, flex: 1, ...valueStyle }}>{value || '—'}</span>
    </div>
  );
}

// ─── Send Modal ───────────────────────────────────────────────────────────────

function SendModal({ instruction, project, profile, attachments, onClose, onSuccess }) {
  const isEWN = instruction.type === 'EWN';
  const [form, setForm] = useState({
    recipientName: '',
    recipientEmail: '',
    recipientCompany: project?.main_contractor || '',
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  function update(field, value) { setForm(f => ({ ...f, [field]: value })); }

  async function handleSend(e) {
    e.preventDefault();
    setError('');
    if (!form.recipientName.trim()) { setError('Recipient name is required.'); return; }
    if (!form.recipientEmail.trim()) { setError('Recipient email is required.'); return; }
    setSending(true);

    try {
      const projectName = `${project?.reference || ''} — ${project?.name || ''}`.trim().replace(/^—\s*/, '');

      // Generate PDF
      const { base64: pdfBase64, fileName: pdfFileName } = await generateInstructionPDF({
        instruction,
        projectName,
        projectRef: project?.reference,
        mcName: project?.main_contractor,
        raisedBy: instruction.profiles?.full_name,
        attachments: attachments || [],
        skipDownload: true,
      });

      // Create portal response token
      const token = crypto.randomUUID();
      const { error: tokenErr } = await supabase
        .from('ewn_cvi_responses')
        .insert({
          instruction_id: instruction.id,
          token,
          recipient_name: form.recipientName,
          recipient_email: form.recipientEmail,
          recipient_company: form.recipientCompany || null,
        });
      if (tokenErr) throw new Error('Failed to create response token: ' + tokenErr.message);

      const portalLink = `https://proline.project-management.uk/mc-portal/ewn-cvi/${instruction.id}?token=${token}`;

      // Build email
      const emailFn = isEWN ? ewnSubmissionEmail : cviSubmissionEmail;
      const { subject, htmlBody } = emailFn({
        ewn: { ...instruction, project_name: projectName },
        cvi: { ...instruction, project_name: projectName },
        recipientName: form.recipientName,
        senderName: profile?.full_name || 'Proline Internals Ltd',
        projectName,
        portalLink,
        attachmentFilenames: (attachments || []).map(a => a.file_name),
      });

      await sendEmail({
        to: form.recipientEmail,
        subject,
        htmlBody,
        attachments: [{ name: pdfFileName, contentType: 'application/pdf', content: pdfBase64 }],
      });

      // Update instruction record
      const isResend = !!(instruction.sent_at);
      const newStatus = isResend ? instruction.status : 'awaiting_instruction';
      await supabase
        .from('instructions')
        .update({
          sent_to_name: form.recipientName,
          sent_to_email: form.recipientEmail,
          sent_at: new Date().toISOString(),
          sent_by: profile.id,
          awaiting_instruction: true,
          status: newStatus,
        })
        .eq('id', instruction.id);

      // Log
      await supabase.from('instruction_submission_log').insert({
        instruction_id: instruction.id,
        sent_by: profile.id,
        sent_to_name: form.recipientName,
        sent_to_email: form.recipientEmail,
        sent_to_company: form.recipientCompany,
        is_resend: isResend,
      });

      onSuccess({ recipientName: form.recipientName, recipientEmail: form.recipientEmail });
    } catch (err) {
      setError(err.message || 'Failed to send. Please try again.');
      setSending(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: '24px',
    }}>
      <div style={{ ...S.card, maxWidth: '520px', width: '100%', padding: '28px' }}>
        <h2 style={{ ...S.sectionTitle, marginBottom: '6px' }}>
          Send {instruction.type} to Main Contractor
        </h2>
        <p style={{ fontSize: '13px', color: C.textMuted, marginBottom: '20px' }}>
          {instruction.reference} — {TYPE_LABEL[instruction.type]}
        </p>

        <form onSubmit={handleSend}>
          {error && <div style={S.alertError}>{error}</div>}

          <div style={S.formGroup}>
            <label style={S.label}>Recipient Name <span style={{ color: C.danger }}>*</span></label>
            <input style={S.input} value={form.recipientName} onChange={e => update('recipientName', e.target.value)} placeholder="e.g. John Smith" required />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Recipient Email <span style={{ color: C.danger }}>*</span></label>
            <input type="email" style={S.input} value={form.recipientEmail} onChange={e => update('recipientEmail', e.target.value)} placeholder="e.g. j.smith@contractor.co.uk" required />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Recipient Company</label>
            <input style={S.input} value={form.recipientCompany} onChange={e => update('recipientCompany', e.target.value)} placeholder="Main contractor company name" />
          </div>

          <div style={{ ...S.alertInfo, fontSize: '13px', marginBottom: '16px' }}>
            An email with the {instruction.type} document as a PDF attachment and a response portal link
            will be sent to the recipient. The status will update to "Awaiting Instruction".
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} disabled={sending} style={{ ...S.btn, ...S.btnGhost }}>Cancel</button>
            <button type="submit" disabled={sending} style={{ ...S.btn, ...S.btnPrimary, opacity: sending ? 0.7 : 1 }}>
              {sending ? 'Sending…' : `Send ${instruction.type}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function InstructionDetail({ navigate, params }) {
  const { profile } = useAuth();
  const instructionId = params?.instructionId;
  const [instruction, setInstruction] = useState(null);
  const [project, setProject] = useState(null);
  const [linkedIR, setLinkedIR] = useState(null);
  const [parentInstruction, setParentInstruction] = useState(null);
  const [submissionLog, setSubmissionLog] = useState([]);
  const [linkedDayworks, setLinkedDayworks] = useState([]);
  const [linkedVariations, setLinkedVariations] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rates, setRates] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  const [bgUploading, setBgUploading] = useState(false);
  const [bgUploadCount, setBgUploadCount] = useState(0);
  const [bgUploadDone, setBgUploadDone] = useState(0);

  const role = profile?.role || '';
  const isProlineStaff = role === 'admin' || role === 'proline_sm' || role === 'proline_qs';

  useEffect(() => {
    if (instructionId) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructionId]);

  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => {
      setLoading(false);
      setError('Loading timed out — please refresh the page or sign in again.');
    }, 8000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    const pending = window.__pendingUploads?.[instructionId];
    if (!pending?.length) return;
    delete window.__pendingUploads[instructionId];
    uploadFilesInBackground(instructionId, pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructionId]);

  async function fetchAttachments() {
    const { data } = await supabase
      .from('instruction_attachments')
      .select('*')
      .eq('instruction_id', instructionId)
      .order('created_at');
    setAttachments(data || []);
  }

  async function uploadFilesInBackground(recordId, files) {
    setBgUploading(true);
    setBgUploadCount(files.length);
    setBgUploadDone(0);
    for (const file of files) {
      try {
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `${recordId}/${Date.now()}_${safeName}`;
        const buffer = await file.arrayBuffer();
        const { error: storageErr } = await supabase.storage
          .from('instruction-attachments')
          .upload(path, buffer, { contentType: file.type || 'application/octet-stream', upsert: false });
        if (!storageErr) {
          await supabase.from('instruction_attachments').insert({
            instruction_id: recordId,
            uploaded_by: profile.id,
            file_name: file.name,
            file_size: file.size,
            content_type: file.type || 'application/octet-stream',
            storage_path: path,
          });
          setBgUploadDone(prev => prev + 1);
          fetchAttachments();
        }
      } catch (err) {
        console.error('Upload error:', file.name, err.message);
      }
    }
    setBgUploading(false);
  }

  async function loadData() {
    setLoading(true);
    try {
      const [{ data: ins }, { data: atts }, { data: dws }] = await Promise.all([
        supabase
          .from('instructions')
          .select('*, projects(id, name, reference, main_contractor), profiles!instructions_created_by_fkey(full_name)')
          .eq('id', instructionId)
          .single(),
        supabase.from('instruction_attachments').select('*').eq('instruction_id', instructionId).order('created_at'),
        supabase
          .from('dayworks')
          .select('id, reference, date_of_works, status, daywork_labour(*), daywork_materials(*), daywork_plant(*)')
          .eq('instruction_id', instructionId)
          .order('date_of_works'),
      ]);

      setInstruction(ins);
      setProject(ins?.projects || null);
      setAttachments(atts || []);
      setLinkedDayworks(dws || []);

      if (ins?.project_id) {
        fetchLabourRates(ins.project_id).then(setRates).catch(console.error);
      }

      // Submission log
      const { data: log } = await supabase
        .from('instruction_submission_log')
        .select('*, profiles!instruction_submission_log_sent_by_fkey(full_name)')
        .eq('instruction_id', instructionId)
        .order('sent_at', { ascending: false });
      setSubmissionLog(log || []);

      // Linked variations
      const { data: vars } = await supabase
        .from('variations')
        .select('id, reference, title, status, value')
        .eq('instruction_id', instructionId)
        .order('created_at');
      setLinkedVariations(vars || []);

      // For IR: load parent EWN/CVI
      if (ins?.parent_instruction_id || ins?.linked_ewn_cvi_id) {
        const parentId = ins.linked_ewn_cvi_id || ins.parent_instruction_id;
        const { data: parent } = await supabase
          .from('instructions')
          .select('id, reference, type, status')
          .eq('id', parentId)
          .single();
        setParentInstruction(parent);
      }

      // For EWN/CVI: load linked IR
      if (ins?.type === 'EWN' || ins?.type === 'CVI') {
        const { data: children } = await supabase
          .from('instructions')
          .select('id, reference, type, status, created_at')
          .or(`parent_instruction_id.eq.${instructionId},linked_ewn_cvi_id.eq.${instructionId}`)
          .eq('type', 'IR')
          .order('created_at')
          .limit(1);
        if (children?.length > 0) setLinkedIR(children[0]);
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleDownload(att) {
    try {
      const { data, error: dlErr } = await supabase.storage
        .from('instruction-attachments')
        .download(att.storage_path);
      if (dlErr) throw dlErr;
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    }
  }

  async function handleDownloadPDF() {
    setGeneratingPDF(true);
    try {
      await generateInstructionPDF({
        instruction,
        projectName: `${project?.reference || ''} — ${project?.name || ''}`.replace(/^— /, ''),
        projectRef: project?.reference,
        mcName: project?.main_contractor,
        raisedBy: instruction.profiles?.full_name,
        attachments,
      });
    } catch (err) {
      console.error('PDF error:', err);
    } finally {
      setGeneratingPDF(false);
    }
  }

  function handleSendSuccess({ recipientName, recipientEmail }) {
    setShowSendModal(false);
    setSendSuccess({ recipientName, recipientEmail, at: new Date().toISOString() });
    loadData();
  }

  if (loading) return <div style={S.spinner}>Loading instruction...</div>;
  if (error) return (
    <div style={{ padding: '20px' }}>
      <div style={{ color: '#dc2626', marginBottom: '12px' }}>{error}</div>
      <button onClick={() => window.location.reload()} style={{ padding: '8px 16px', background: '#374151', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
        Refresh Page
      </button>
    </div>
  );
  if (!instruction) return (
    <div style={S.pageContainer}>
      <div style={S.alertError}>Instruction not found.</div>
      <button onClick={() => navigate('projects', {})} style={{ ...S.btn, ...S.btnGhost }}>← Back to Projects</button>
    </div>
  );

  const statusStyle = getStatusBadgeStyle(instruction.status);
  const typeBadgeStyle = TYPE_STYLE[instruction.type] || TYPE_STYLE.EWN;
  const wording = WORDING[instruction.type] || EWN_WORDING;
  const isEWNorCVI = instruction.type === 'EWN' || instruction.type === 'CVI';
  const isIR = instruction.type === 'IR';
  const alreadySent = !!(instruction.sent_at);
  const isSentOrAwaiting = alreadySent || instruction.awaiting_instruction;

  return (
    <div style={S.pageContainer}>
      {showSendModal && (
        <SendModal
          instruction={instruction}
          project={project}
          profile={profile}
          attachments={attachments}
          onClose={() => setShowSendModal(false)}
          onSuccess={handleSendSuccess}
        />
      )}

      <button
        onClick={() => navigate('project-detail', { projectId: instruction.project_id })}
        style={{ ...S.btn, ...S.btnGhost, ...S.btnSm, marginBottom: '16px' }}
      >
        ← Back to Project
      </button>

      {instruction.raised_by_mc && (
        <div style={{ background: '#f0f9ff', border: '1px solid #38bdf8', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', fontWeight: '700', background: '#0ea5e9', color: '#fff', padding: '2px 8px', borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>MC Raised</span>
          <span style={{ fontSize: '14px', color: '#0c4a6e' }}>
            This instruction was raised by the Main Contractor
            {instruction.mc_instruction_ref ? ` — MC Ref: ${instruction.mc_instruction_ref}` : ''}
          </span>
        </div>
      )}

      {bgUploading && (
        <div style={{
          background: '#f5f5f5', border: '1px solid #d1d5db',
          borderRadius: '6px', padding: '10px 16px', marginBottom: '16px',
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          <span>⏳</span>
          <span style={{ fontSize: '14px', color: '#111111' }}>
            Uploading attachments… {bgUploadDone} of {bgUploadCount} complete
          </span>
        </div>
      )}

      {/* ── HEADER ────────────────────────────────────────────────────────── */}
      <div style={{ background: '#111827', borderRadius: '10px', padding: '20px 24px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ ...typeBadgeStyle, padding: '4px 10px', borderRadius: '4px', fontWeight: '800', fontSize: '13px', letterSpacing: '0.05em' }}>
              {instruction.type}
            </span>
            <span style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', padding: '6px 16px', borderRadius: '6px', fontFamily: 'monospace', fontSize: '20px', fontWeight: '700', letterSpacing: '0.08em' }}>
              {instruction.reference}
            </span>
            <span style={{ ...S.badge, ...statusStyle }}>
              {instruction.status?.replace(/_/g, ' ')}
            </span>
            {instruction.date && (
              <span style={{ background: 'rgba(255,255,255,0.1)', color: '#e5e7eb', padding: '5px 12px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                {formatDate(instruction.date)}
              </span>
            )}
          </div>

          <button
            onClick={handleDownloadPDF}
            disabled={generatingPDF}
            style={{ ...S.btn, background: '#fff', color: '#111', fontSize: '13px', fontWeight: '600', opacity: generatingPDF ? 0.7 : 1 }}
          >
            {generatingPDF ? 'Generating…' : 'Download PDF'}
          </button>
        </div>

        <div style={{ fontSize: '13px', color: '#9ca3af' }}>
          <span style={{ fontWeight: '600', color: '#d1d5db' }}>{TYPE_LABEL[instruction.type]}</span>
          {' · '}
          {project?.reference} — {project?.name}
        </div>
      </div>

      {/* ── EWN / CVI ACTION BAR ──────────────────────────────────────────── */}
      {isEWNorCVI && isProlineStaff && (
        <div style={{ ...S.card, marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            {!alreadySent ? (
              <button
                onClick={() => setShowSendModal(true)}
                style={{ ...S.btn, background: '#111827', color: '#fff', fontSize: '14px', fontWeight: '600' }}
              >
                Send to Main Contractor
              </button>
            ) : (
              <>
                <button
                  onClick={() => setShowSendModal(true)}
                  style={{ ...S.btn, background: '#111827', color: '#fff', fontSize: '14px', fontWeight: '600' }}
                >
                  Resend / Send to Another Recipient
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── SEND STATUS BANNER ────────────────────────────────────────────── */}
      {isEWNorCVI && (
        <div style={{
          ...S.card, marginBottom: '20px',
          background: isSentOrAwaiting ? C.successLight : C.warningLight,
          border: `1px solid ${isSentOrAwaiting ? C.successBorder : C.warningBorder}`,
        }}>
          {isSentOrAwaiting ? (
            <div>
              <div style={{ fontWeight: '700', fontSize: '14px', color: C.success, marginBottom: '4px' }}>
                Sent to Main Contractor
              </div>
              <div style={{ fontSize: '13px', color: C.text }}>
                {instruction.sent_to_name && <span>Sent to <strong>{instruction.sent_to_name}</strong>{instruction.sent_to_email ? ` (${instruction.sent_to_email})` : ''}</span>}
                {instruction.sent_at && <span>{instruction.sent_to_name ? ' — ' : ''}Sent on {formatDateTime(instruction.sent_at)}</span>}
              </div>
              {(sendSuccess) && (
                <div style={{ fontSize: '13px', color: C.success, marginTop: '6px' }}>
                  Just sent to {sendSuccess.recipientName} at {sendSuccess.recipientEmail}
                </div>
              )}
            </div>
          ) : (
            <div>
              <div style={{ fontWeight: '700', fontSize: '14px', color: C.warning, marginBottom: '4px' }}>
                Not yet sent to Main Contractor
              </div>
              <div style={{ fontSize: '13px', color: C.text }}>
                This {instruction.type} has not yet been sent to the Main Contractor. Send it now to create your formal record.
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── MC PORTAL RESPONSE PANEL (EWN/CVI) ───────────────────────────── */}
      {isEWNorCVI && instruction.mc_response && (
        <div style={{
          ...S.card, marginBottom: '20px',
          background: instruction.mc_response === 'will_instruct' ? C.successLight
            : instruction.mc_response === 'query' ? C.warningLight : C.dangerLight,
          border: `1px solid ${
            instruction.mc_response === 'will_instruct' ? C.successBorder
            : instruction.mc_response === 'query' ? C.warningBorder : C.dangerBorder
          }`,
        }}>
          <div style={{
            fontWeight: '700', fontSize: '14px', marginBottom: '8px',
            color: instruction.mc_response === 'will_instruct' ? C.success
              : instruction.mc_response === 'query' ? C.warning : C.danger,
          }}>
            MC Response: {
              instruction.mc_response === 'will_instruct' ? 'Will Issue Instruction'
              : instruction.mc_response === 'query' ? 'Query Raised'
              : 'Disputed'
            }
          </div>
          {instruction.mc_response === 'will_instruct' && instruction.mc_expected_instruction_date && (
            <div style={{ fontSize: '13px', marginBottom: '4px', color: C.text }}>
              Expected by: <strong>{formatDate(instruction.mc_expected_instruction_date)}</strong>
            </div>
          )}
          {instruction.mc_response_at && (
            <div style={{ fontSize: '13px', color: C.textMuted, marginBottom: '4px' }}>
              Responded: {formatDateTime(instruction.mc_response_at)}
            </div>
          )}
          {instruction.mc_response_notes && (
            <div style={{ fontSize: '13px', color: C.text, marginTop: '8px', padding: '8px 12px', background: 'rgba(255,255,255,0.6)', borderRadius: '4px' }}>
              {instruction.mc_response_notes}
            </div>
          )}
          {instruction.mc_response === 'will_instruct' && isProlineStaff && !linkedIR && (
            <button
              onClick={() => navigate('instruction-new', {
                projectId: instruction.project_id,
                followUpFromId: instructionId,
                followUpFromRef: instruction.reference,
                followUpFromType: instruction.type,
              })}
              style={{ ...S.btn, ...S.btnSm, background: '#065f46', color: '#fff', marginTop: '12px' }}
            >
              Record Formal Instruction / Raise IR
            </button>
          )}
        </div>
      )}

      {/* ── MC RESPONSE / AWAITING INSTRUCTION SECTION (EWN/CVI) ─────────── */}
      {isEWNorCVI && (
        <div style={{
          ...S.card, marginBottom: '20px',
          background: linkedIR ? C.successLight : '#f0f9ff',
          border: `1px solid ${linkedIR ? C.successBorder : '#bae6fd'}`,
        }}>
          {linkedIR ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <span style={{ fontSize: '16px' }}>✓</span>
                <span style={{ fontWeight: '700', fontSize: '14px', color: C.success }}>MC has responded — IR recorded</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '13px', color: C.textMuted }}>Linked IR:</span>
                <button
                  onClick={() => navigate('instruction-detail', { instructionId: linkedIR.id })}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <span style={{ ...TYPE_STYLE.IR, padding: '2px 8px', borderRadius: '3px', fontWeight: '700', fontSize: '12px' }}>IR</span>
                  <span style={{ fontWeight: '600', color: C.accent, fontSize: '14px', textDecoration: 'underline' }}>{linkedIR.reference}</span>
                  <span style={{ ...S.badge, ...getStatusBadgeStyle(linkedIR.status) }}>{linkedIR.status?.replace(/_/g, ' ')}</span>
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontWeight: '700', fontSize: '14px', color: '#0369a1', marginBottom: '4px' }}>
                Awaiting formal written instruction from Main Contractor
              </div>
              <div style={{ fontSize: '13px', color: C.textMuted, marginBottom: '12px' }}>
                {instruction.type === 'EWN'
                  ? 'Once the MC issues a formal written instruction, record it as an IR linked to this EWN.'
                  : 'Once the MC confirms in writing, record the formal instruction as an IR linked to this CVI.'}
              </div>
              {isProlineStaff && !instruction.mc_response && (
                <button
                  onClick={() => navigate('instruction-new', {
                    projectId: instruction.project_id,
                    followUpFromId: instructionId,
                    followUpFromRef: instruction.reference,
                    followUpFromType: instruction.type,
                  })}
                  style={{ ...S.btn, ...S.btnSm, background: '#065f46', color: '#fff', borderColor: '#065f46' }}
                >
                  Record MC Response / Raise IR
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── IR: LINKED PARENT EWN/CVI ─────────────────────────────────────── */}
      {isIR && parentInstruction && (
        <div style={{ ...S.card, marginBottom: '20px', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', color: '#065f46', fontWeight: '600' }}>Raised in response to:</span>
            <button
              onClick={() => navigate('instruction-detail', { instructionId: parentInstruction.id })}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <span style={{ ...TYPE_STYLE[parentInstruction.type], padding: '2px 8px', borderRadius: '3px', fontWeight: '700', fontSize: '12px' }}>
                {parentInstruction.type}
              </span>
              <span style={{ fontWeight: '600', color: C.accent, fontSize: '14px', textDecoration: 'underline' }}>
                {parentInstruction.reference}
              </span>
              <span style={{ ...S.badge, ...getStatusBadgeStyle(parentInstruction.status) }}>
                {parentInstruction.status?.replace(/_/g, ' ')}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* ── EWN DETAILS ───────────────────────────────────────────────────── */}
      {instruction.type === 'EWN' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px', marginBottom: '20px' }}>
            <div style={S.card}>
              <h3 style={{ ...S.sectionTitle, marginBottom: '14px' }}>EWN Details</h3>
              <InfoRow label="Date Noticed" value={formatDate(instruction.date)} />
              {instruction.time && <InfoRow label="Time Noticed" value={instruction.time} />}
              {instruction.location && <InfoRow label="Location" value={instruction.location} />}
              {instruction.gridlines && <InfoRow label="Gridlines" value={instruction.gridlines} />}
              {instruction.level && <InfoRow label="Level / Floor" value={instruction.level} />}
              <hr style={S.divider} />
              <InfoRow label="Raised By" value={instruction.profiles?.full_name} />
              <InfoRow label="Date Created" value={formatDateTime(instruction.created_at)} />
            </div>
            {(instruction.potential_impact?.length > 0 || instruction.action_requested) && (
              <div style={S.card}>
                {instruction.potential_impact?.length > 0 && (
                  <>
                    <h3 style={{ ...S.sectionTitle, marginBottom: '12px' }}>Potential Impact</h3>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: instruction.action_requested ? '16px' : 0 }}>
                      {instruction.potential_impact.map(v => {
                        const col = IMPACT_COLOURS[v] || { bg: '#f3f4f6', color: '#374151', border: '#e5e7eb' };
                        return (
                          <span key={v} style={{
                            padding: '4px 12px', borderRadius: '12px', fontSize: '13px', fontWeight: '600',
                            background: col.bg, color: col.color, border: `1px solid ${col.border}`,
                          }}>
                            {IMPACT_LABELS[v] || v}
                          </span>
                        );
                      })}
                    </div>
                    {instruction.action_requested && <hr style={S.divider} />}
                  </>
                )}
                {instruction.action_requested && (
                  <>
                    <h3 style={{ ...S.sectionTitle, marginBottom: '10px', fontSize: '14px' }}>Action Requested from MC</h3>
                    <p style={{ fontSize: '14px', color: C.text, lineHeight: '1.7', whiteSpace: 'pre-wrap', margin: 0 }}>
                      {instruction.action_requested}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>

          <div style={{ ...S.card, marginBottom: '20px' }}>
            <h3 style={{ ...S.sectionTitle, marginBottom: '12px' }}>Description of Issue</h3>
            <p style={{ fontSize: '14px', color: C.text, lineHeight: '1.7', whiteSpace: 'pre-wrap', margin: 0 }}>
              {instruction.description || '—'}
            </p>
          </div>
        </>
      )}

      {/* ── CVI DETAILS ───────────────────────────────────────────────────── */}
      {instruction.type === 'CVI' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px', marginBottom: '20px' }}>
            <div style={S.card}>
              <h3 style={{ ...S.sectionTitle, marginBottom: '14px' }}>Verbal Instruction Details</h3>
              <InfoRow label="Date of Verbal Instruction" value={formatDate(instruction.verbal_date || instruction.date)} />
              {(instruction.verbal_time || instruction.time) && (
                <InfoRow label="Time" value={instruction.verbal_time || instruction.time} />
              )}
              {instruction.verbal_method && <InfoRow label="How Given" value={instruction.verbal_method} />}
              <hr style={S.divider} />
              {instruction.instructed_by_name && <InfoRow label="Given By" value={instruction.instructed_by_name} />}
              {instruction.instructed_by_role && <InfoRow label="Their Role" value={instruction.instructed_by_role} />}
            </div>
            <div style={S.card}>
              <h3 style={{ ...S.sectionTitle, marginBottom: '14px' }}>Confirmed By (Proline)</h3>
              <InfoRow label="Confirmed By" value={instruction.profiles?.full_name} />
              <InfoRow label="Date Confirmed" value={formatDateTime(instruction.created_at)} />
            </div>
          </div>

          <div style={{ ...S.card, marginBottom: '20px' }}>
            <h3 style={{ ...S.sectionTitle, marginBottom: '12px' }}>Works Verbally Instructed</h3>
            <p style={{ fontSize: '14px', color: C.text, lineHeight: '1.7', whiteSpace: 'pre-wrap', margin: 0 }}>
              {instruction.description || '—'}
            </p>
          </div>
        </>
      )}

      {/* ── IR DETAILS ────────────────────────────────────────────────────── */}
      {instruction.type === 'IR' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px', marginBottom: '20px' }}>
            <div style={S.card}>
              <h3 style={{ ...S.sectionTitle, marginBottom: '14px' }}>Instruction Details</h3>
              <InfoRow label="Date Received" value={formatDate(instruction.date)} />
              {instruction.time && <InfoRow label="Time" value={instruction.time} />}
              {instruction.instruction_method && <InfoRow label="How Received" value={instruction.instruction_method} />}
              {instruction.location && <InfoRow label="Location" value={instruction.location} />}
              {instruction.gridlines && <InfoRow label="Gridlines" value={instruction.gridlines} />}
              {instruction.level && <InfoRow label="Level / Floor" value={instruction.level} />}
            </div>
            <div style={S.card}>
              <h3 style={{ ...S.sectionTitle, marginBottom: '14px' }}>Issued By (MC)</h3>
              {instruction.instructed_by_name && <InfoRow label="Name" value={instruction.instructed_by_name} />}
              {instruction.instructed_by_role && <InfoRow label="Role / Position" value={instruction.instructed_by_role} />}
              <hr style={S.divider} />
              <InfoRow label="Recorded By" value={instruction.profiles?.full_name} />
              <InfoRow label="Date Recorded" value={formatDateTime(instruction.created_at)} />
            </div>
          </div>

          <div style={{ ...S.card, marginBottom: '20px' }}>
            <h3 style={{ ...S.sectionTitle, marginBottom: '12px' }}>Description of Works Instructed</h3>
            <p style={{ fontSize: '14px', color: C.text, lineHeight: '1.7', whiteSpace: 'pre-wrap', margin: 0 }}>
              {instruction.description || '—'}
            </p>
          </div>

          {instruction.reason_outside_scope && (
            <div style={{ ...S.card, marginBottom: '20px' }}>
              <h3 style={{ ...S.sectionTitle, marginBottom: '12px' }}>Reason Works Are Outside Subcontract Scope</h3>
              <p style={{ fontSize: '14px', color: C.text, lineHeight: '1.7', whiteSpace: 'pre-wrap', margin: 0 }}>
                {instruction.reason_outside_scope}
              </p>
            </div>
          )}
        </>
      )}

      {/* ── VALUATION ACTIONS (IR only) ────────────────────────────────────── */}
      {isIR && isProlineStaff && (
        <div style={{ ...S.card, marginBottom: '20px', background: '#f8fafc', border: `1px solid ${C.border}` }}>
          <div style={{ fontWeight: '600', fontSize: '14px', color: C.text, marginBottom: '12px' }}>
            How will this instruction be valued?
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('daywork-new', { projectId: instruction.project_id, instructionId })}
              style={{ ...S.btn, ...S.btnOutline, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '10px 18px' }}
            >
              <div style={{ fontWeight: '600', fontSize: '14px' }}>Record as Dayworks</div>
              <div style={{ fontSize: '12px', color: C.textMuted, fontWeight: '400' }}>Log daily labour, materials &amp; plant</div>
            </button>
            <button
              onClick={() => navigate('variation-new', { projectId: instruction.project_id, instructionId })}
              style={{ ...S.btn, ...S.btnOutline, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '10px 18px' }}
            >
              <div style={{ fontWeight: '600', fontSize: '14px' }}>Raise Variation</div>
              <div style={{ fontSize: '12px', color: C.textMuted, fontWeight: '400' }}>Submit a priced variation account</div>
            </button>
          </div>
        </div>
      )}

      {/* ── LINKED DAYWORKS ───────────────────────────────────────────────── */}
      {linkedDayworks.length > 0 && (
        <div style={{ ...S.card, marginBottom: '20px' }}>
          <div style={{ ...S.sectionHeader, marginBottom: '12px' }}>
            <h3 style={{ ...S.sectionTitle, margin: 0 }}>Linked Dayworks ({linkedDayworks.length})</h3>
          </div>
          <div style={S.tableWrapper}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Reference</th>
                  <th style={S.th}>Date</th>
                  <th style={S.th}>Status</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Hours</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Value</th>
                  <th style={S.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {linkedDayworks.map(dw => {
                  const totalHours = (dw.daywork_labour || []).reduce((sum, l) => sum + (parseFloat(l.hours_calculated) || 0), 0);
                  const value = rates ? calcDayworkSheetCost(dw.daywork_labour, dw.daywork_materials, dw.daywork_plant, rates) : null;
                  const st = getStatusBadgeStyle(dw.status);
                  return (
                    <tr key={dw.id} style={{ cursor: 'pointer' }}
                      onClick={() => navigate('daywork-detail', { dayworkId: dw.id })}
                      onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={S.td}><span style={{ fontWeight: '600', color: C.accent }}>{dw.reference}</span></td>
                      <td style={S.td}>{formatDate(dw.date_of_works)}</td>
                      <td style={S.td}><span style={{ ...S.badge, ...st }}>{dw.status}</span></td>
                      <td style={{ ...S.td, textAlign: 'right', fontWeight: '600' }}>
                        {totalHours > 0 ? `${fmtHours(totalHours)}h` : '—'}
                      </td>
                      <td style={{ ...S.td, textAlign: 'right', fontWeight: '600', color: value > 0 ? C.success : C.textMuted }}>
                        {value === null ? <span style={{ color: C.textMuted, fontSize: '12px' }}>...</span>
                          : value > 0 ? formatCurrency(value) : '—'}
                      </td>
                      <td style={S.td} onClick={e => e.stopPropagation()}>
                        <button onClick={() => navigate('daywork-detail', { dayworkId: dw.id })} style={{ ...S.btn, ...S.btnGhost, ...S.btnSm }}>View</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── LINKED VARIATIONS ─────────────────────────────────────────────── */}
      {linkedVariations.length > 0 && (
        <div style={{ ...S.card, marginBottom: '20px' }}>
          <h3 style={{ ...S.sectionTitle, marginBottom: '12px' }}>Linked Variations ({linkedVariations.length})</h3>
          <div style={S.tableWrapper}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Reference</th>
                  <th style={S.th}>Title</th>
                  <th style={S.th}>Status</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Value</th>
                </tr>
              </thead>
              <tbody>
                {linkedVariations.map(v => (
                  <tr key={v.id} style={{ cursor: 'pointer' }}
                    onClick={() => navigate('variation-detail', { variationId: v.id })}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={S.td}><span style={{ fontWeight: '600', color: C.accent }}>{v.reference}</span></td>
                    <td style={S.td}>{v.title || '—'}</td>
                    <td style={S.td}><span style={{ ...S.badge, ...getStatusBadgeStyle(v.status) }}>{v.status}</span></td>
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: '600', color: C.success }}>{formatCurrency(v.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── ATTACHMENTS ───────────────────────────────────────────────────── */}
      <div style={{ ...S.card, marginBottom: '20px' }}>
        <h3 style={{ ...S.sectionTitle, marginBottom: '16px' }}>
          Attachments ({attachments.length})
          {isIR && attachments.length === 0 && (
            <span style={{ fontSize: '12px', fontWeight: '400', color: C.warning, marginLeft: '10px' }}>
              — Attach the original instruction for your contractual record
            </span>
          )}
        </h3>
        {isProlineStaff && (
          <div style={{ marginBottom: '16px' }}>
            <AttachmentUploader
              recordId={instruction.id}
              bucket="instruction-attachments"
              tableName="instruction_attachments"
              foreignKey="instruction_id"
              fileTypeField="content_type"
              onUploadSuccess={fetchAttachments}
              label="instruction documents"
            />
          </div>
        )}
        {attachments.length === 0 ? (
          <p style={{ fontSize: '13px', color: C.textMuted, margin: 0 }}>No attachments yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {attachments.map(att => (
              <div key={att.id} style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '10px 12px', borderRadius: '6px',
                border: `1px solid ${C.border}`, background: '#fafafa',
              }}>
                <FileIcon type={att.content_type} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: '500', color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {att.file_name}
                  </div>
                  <div style={{ fontSize: '12px', color: C.textMuted }}>
                    {formatBytes(att.file_size)}{att.file_size ? ' · ' : ''}{formatDateTime(att.created_at)}
                  </div>
                </div>
                <button type="button" onClick={() => handleDownload(att)} style={{ ...S.btn, ...S.btnOutline, ...S.btnSm }}>
                  Download
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── SUBMISSION LOG (EWN / CVI) ─────────────────────────────────────── */}
      {isEWNorCVI && submissionLog.length > 0 && (
        <details style={{ ...S.card, marginBottom: '20px' }}>
          <summary style={{ fontWeight: '600', fontSize: '14px', cursor: 'pointer', color: C.text, padding: '4px 0' }}>
            Submission History ({submissionLog.length})
          </summary>
          <div style={{ marginTop: '14px' }}>
            {submissionLog.map((log, i) => (
              <div key={log.id || i} style={{
                display: 'flex', gap: '12px', padding: '10px 0',
                borderBottom: i < submissionLog.length - 1 ? `1px solid ${C.border}` : 'none',
                fontSize: '13px',
              }}>
                <div style={{ color: C.textMuted, minWidth: '140px' }}>{formatDateTime(log.sent_at)}</div>
                <div>
                  <span style={{ fontWeight: '600' }}>
                    {log.is_resend ? 'Resent' : 'Sent'} to {log.sent_to_name}
                  </span>
                  {log.sent_to_email && <span style={{ color: C.textMuted }}> ({log.sent_to_email})</span>}
                  {log.sent_to_company && <span style={{ color: C.textMuted }}> — {log.sent_to_company}</span>}
                  {log.profiles?.full_name && <div style={{ color: C.textMuted, fontSize: '12px', marginTop: '2px' }}>by {log.profiles.full_name}</div>}
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* ── LEGAL WORDING ─────────────────────────────────────────────────── */}
      <div style={{ ...S.card, marginBottom: '20px' }}>
        <h3 style={{ ...S.sectionTitle, marginBottom: '12px' }}>
          {TYPE_LABEL[instruction.type]} — Standard Wording
        </h3>
        <div style={{
          background: '#f9fafb', border: `1px solid ${C.border}`, borderRadius: '6px',
          padding: '16px', fontSize: '13px', lineHeight: '1.7',
          color: C.textMuted, whiteSpace: 'pre-line', fontStyle: 'italic',
        }}>
          {wording}
        </div>
      </div>
    </div>
  );
}
